// ============ Shared terrain height & noise for the terrain lab ============

export const RADIUS = 10_000

// Permutation-table 3D gradient noise
const PERM = new Uint8Array(512)
const GRAD3 = [
  1,1,0, -1,1,0, 1,-1,0, -1,-1,0,
  1,0,1, -1,0,1, 1,0,-1, -1,0,-1,
  0,1,1, 0,-1,1, 0,1,-1, 0,-1,-1,
]
{
  const p = new Uint8Array(256)
  for (let i = 0; i < 256; i++) p[i] = i
  let seed = 42
  for (let i = 255; i > 0; i--) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    const j = seed % (i + 1)
    const tmp = p[i]; p[i] = p[j]; p[j] = tmp
  }
  for (let i = 0; i < 512; i++) PERM[i] = p[i & 255]
}

export function noise3(x: number, y: number, z: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z)
  const fx = x - ix, fy = y - iy, fz = z - iz
  const X = ix & 255, Y = iy & 255, Z = iz & 255

  const u = fx * fx * fx * (fx * (fx * 6 - 15) + 10)
  const v = fy * fy * fy * (fy * (fy * 6 - 15) + 10)
  const w = fz * fz * fz * (fz * (fz * 6 - 15) + 10)

  const grad = (hash: number, dx: number, dy: number, dz: number) => {
    const i = (hash & 11) * 3
    return GRAD3[i] * dx + GRAD3[i + 1] * dy + GRAD3[i + 2] * dz
  }

  const aa = PERM[PERM[X] + Y] + Z
  const ab = PERM[PERM[X] + Y + 1] + Z
  const ba = PERM[PERM[X + 1] + Y] + Z
  const bb = PERM[PERM[X + 1] + Y + 1] + Z

  const l = (a: number, b: number, t: number) => a + t * (b - a)
  return l(
    l(l(grad(PERM[aa], fx, fy, fz), grad(PERM[ba], fx - 1, fy, fz), u),
      l(grad(PERM[ab], fx, fy - 1, fz), grad(PERM[bb], fx - 1, fy - 1, fz), u), v),
    l(l(grad(PERM[aa + 1], fx, fy, fz - 1), grad(PERM[ba + 1], fx - 1, fy, fz - 1), u),
      l(grad(PERM[ab + 1], fx, fy - 1, fz - 1), grad(PERM[bb + 1], fx - 1, fy - 1, fz - 1), u), v),
    w,
  )
}

export function lerp(a: number, b: number, t: number): number { return a + t * (b - a) }
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

export function fbm(nx: number, ny: number, nz: number, octaves: number, baseFreq: number, persistence: number, seed: number): number {
  let h = 0, amp = 1, freq = baseFreq, total = 0
  for (let i = 0; i < octaves; i++) {
    h += noise3(nx * freq + seed, ny * freq + seed * 0.73, nz * freq + seed * 0.37) * amp
    total += amp; amp *= persistence; freq *= 2.0
  }
  return h / total
}

export function ridgedFBM(nx: number, ny: number, nz: number, octaves: number, baseFreq: number, lacunarity: number, seed: number): number {
  let result = 0, amp = 1, freq = baseFreq, weight = 1, total = 0
  for (let i = 0; i < octaves; i++) {
    let signal = noise3(nx * freq + seed, ny * freq + seed * 0.73, nz * freq + seed * 0.37)
    signal = 1.0 - Math.abs(signal)
    signal *= signal
    signal *= weight
    weight = Math.min(1, Math.max(0, signal * 2.0))
    result += signal * amp; total += amp; amp *= 0.5; freq *= lacunarity
  }
  return result / total
}

export function billowFBM(nx: number, ny: number, nz: number, octaves: number, baseFreq: number, persistence: number, seed: number): number {
  let h = 0, amp = 1, freq = baseFreq, total = 0
  for (let i = 0; i < octaves; i++) {
    h += Math.abs(noise3(nx * freq + seed, ny * freq + seed * 0.73, nz * freq + seed * 0.37)) * amp
    total += amp; amp *= persistence; freq *= 2.0
  }
  return h / total
}

export function terrace(value: number, steps: number): number {
  const k = (value * 0.5 + 0.5) * steps
  const fl = Math.floor(k)
  const f = k - fl
  const t = smoothstep(0.3, 0.7, f)
  return ((fl + t) / steps) * 2 - 1
}

// Island definitions
const ISLANDS = [
  { pos: [ 0.58,  0.58,  0.58], radius: 0.50 },
  { pos: [-0.85,  0.30,  0.43], radius: 0.42 },
  { pos: [ 0.15, -0.90,  0.41], radius: 0.48 },
  { pos: [-0.40,  0.70, -0.59], radius: 0.38 },
  { pos: [ 0.90, -0.20, -0.39], radius: 0.44 },
  { pos: [-0.25, -0.55, -0.80], radius: 0.35 },
  { pos: [ 0.35,  0.25,  0.90], radius: 0.46 },
  { pos: [-0.70, -0.65,  0.30], radius: 0.40 },
].map(isl => {
  const [x, y, z] = isl.pos
  const len = Math.sqrt(x * x + y * y + z * z)
  return { pos: [x / len, y / len, z / len] as number[], radius: isl.radius }
})

/** Unclamped envelope: positive inside islands, negative in ocean */
function rawEnvelopeUnclamped(nx: number, ny: number, nz: number): number {
  const cw = 0.08
  const wnx = nx + cw * noise3(nx * 5 + 13.7, ny * 5 + 7.1, nz * 5 + 3.3)
  const wny = ny + cw * noise3(nx * 5 + 51.3, ny * 5 + 29.7, nz * 5 + 71.1)
  const wnz = nz + cw * noise3(nx * 5 + 89.3, ny * 5 + 61.7, nz * 5 + 41.9)
  const wl = Math.sqrt(wnx * wnx + wny * wny + wnz * wnz)
  const px = wnx / wl, py = wny / wl, pz = wnz / wl

  let best = -Infinity
  for (const isl of ISLANDS) {
    const dot = px * isl.pos[0] + py * isl.pos[1] + pz * isl.pos[2]
    const dist = Math.acos(Math.min(1, Math.max(-1, dot)))
    const t = 1 - dist / isl.radius
    if (t > best) best = t
  }
  return Math.min(1, best)
}

/** Raw linear envelope: 0 at coast edge, 1 deep inland (no smoothstep) */
export function rawIslandEnvelope(nx: number, ny: number, nz: number): number {
  return Math.max(0, rawEnvelopeUnclamped(nx, ny, nz))
}

export function islandEnvelope(nx: number, ny: number, nz: number): number {
  const s = rawIslandEnvelope(nx, ny, nz)
  return s * s * (3 - 2 * s)
}

/** Returns fractional height (multiply by RADIUS for world units) */
export function heightAt(nx: number, ny: number, nz: number): number {
  const OCEAN_FLOOR = -0.04
  const SLOPE_RANGE = 0.15 // unclamped envelope range for underwater slope

  const unclamped = rawEnvelopeUnclamped(nx, ny, nz)

  // Deep ocean — flat floor
  if (unclamped <= -SLOPE_RANGE) return OCEAN_FLOOR

  // Underwater slope — smooth from ocean floor up to sea level
  if (unclamped <= 0) {
    const t = (unclamped + SLOPE_RANGE) / SLOPE_RANGE // 0 at deep edge, 1 at coast
    const s = t * t * (3 - 2 * t) // smoothstep
    return lerp(OCEAN_FLOOR, -0.001, s)
  }

  const rawEnvelope = islandEnvelope(nx, ny, nz)

  // Reshape envelope: steep near coast (thin beach), flat inland (grassland plateau)
  // pow(0.4) makes it rise fast from 0 then flatten — thin beach, wide flat grasslands
  const envelope = Math.pow(rawEnvelope, 0.4)

  const dw = 0.10
  const wx = nx + dw * noise3(nx * 3.5 + 7.3, ny * 3.5 + 2.1, nz * 3.5 + 5.9)
  const wy = ny + dw * noise3(nx * 3.5 + 47.1, ny * 3.5 + 31.7, nz * 3.5 + 19.3)
  const wz = nz + dw * noise3(nx * 3.5 + 91.7, ny * 3.5 + 67.3, nz * 3.5 + 43.1)
  const wl = Math.sqrt(wx * wx + wy * wy + wz * wz)
  const px = wx / wl, py = wy / wl, pz = wz / wl

  const rawMtn = noise3(px * 2.0, py * 2.0, pz * 2.0) * 0.5 + 0.5
  const mountainess = smoothstep(0.28, 0.72, rawMtn)
  const rawRug = noise3(px * 2.5 + 100, py * 2.5 + 100, pz * 2.5 + 100) * 0.5 + 0.5
  const ruggedness = smoothstep(0.25, 0.75, rawRug)

  const base = fbm(px, py, pz, 2, 2.5, 0.5, 0) * 0.012

  const plains = fbm(px, py, pz, 3, 8.0, 0.40, 200) * 0.005
  const badlandsRaw = fbm(px, py, pz, 5, 6.0, 0.50, 600)
  const badlands = terrace(badlandsRaw, 6) * 0.018
  const flatTerrain = lerp(plains, badlands, ruggedness)

  const hills = billowFBM(px, py, pz, 5, 5.0, 0.48, 300) * 0.025
  const foothills = ridgedFBM(px, py, pz, 4, 6.0, 2.0, 350) * 0.03
  const hillTerrain = lerp(hills, foothills, ruggedness)

  const rockies = ridgedFBM(px, py, pz, 7, 4.0, 2.1, 400) * 0.065
  const dolomiteBase = ridgedFBM(px, py, pz, 6, 5.0, 2.0, 500)
  const dolomites = Math.pow(Math.max(0, dolomiteBase), 2.2) * 0.10
  const mountainTerrain = lerp(rockies, dolomites, ruggedness)

  let terrain: number
  if (mountainess < 0.4) {
    terrain = lerp(flatTerrain, hillTerrain, mountainess / 0.4)
  } else {
    terrain = lerp(hillTerrain, mountainTerrain, (mountainess - 0.4) / 0.6)
  }

  const plateauMask = smoothstep(0.3, 0.5, mountainess) * (1 - smoothstep(0.7, 0.9, mountainess))
  const plateauNoise = noise3(px * 3.5 + 150, py * 3.5 + 150, pz * 3.5 + 150) * 0.5 + 0.5
  if (plateauMask > 0.1 && plateauNoise > 0.5) {
    const plateauStrength = plateauMask * smoothstep(0.5, 0.7, plateauNoise) * 0.6
    terrain = lerp(terrain, 0.018, plateauStrength)
  }

  const valleyNoise = ridgedFBM(px, py, pz, 3, 3.0, 2.0, 450)
  const valleyMask = (1 - valleyNoise) * smoothstep(0.5, 0.8, mountainess)
  if (valleyMask > 0.5) {
    const flatten = smoothstep(0.5, 0.8, valleyMask) * 0.4
    terrain = lerp(terrain, terrain * 0.3, flatten)
  }

  const detail = fbm(px, py, pz, 3, 28.0, 0.50, 700) * 0.002
  const microDetail = fbm(px, py, pz, 2, 55.0, 0.45, 800) * 0.0008

  // Inland terrain shaped by envelope and biome
  const inlandH = (base + terrain + detail + microDetail) * envelope + 0.022 * envelope - 0.014

  // Coastal rolling plains: flat base with gentle hills, not sloping toward mountains
  // Only in non-mountain areas near the coast
  const coastProximity = smoothstep(0.45, 0.10, rawEnvelope) // 1 at coast, 0 deep inland
  const coastalWeight = coastProximity * (1 - mountainess * 0.8)

  if (coastalWeight > 0.05) {
    // Gentle rolling hills at a fixed elevation — perfect for villages
    const rolls = fbm(px, py, pz, 4, 14.0, 0.45, 950) * 0.004
    const gentleHills = fbm(px, py, pz, 3, 6.0, 0.4, 970) * 0.002
    const coastalH = 0.006 + rolls + gentleHills
    return lerp(inlandH, coastalH, coastalWeight * 0.75)
  }

  return inlandH
}

/** Sample terrain radius at a unit-sphere direction */
export function sampleTerrainRadius(nx: number, ny: number, nz: number): number {
  const h = heightAt(nx, ny, nz)
  return RADIUS * (1.0 + Math.max(h, 0))
}
